import { useCallback, useState } from "react";
import Modal from "components/elements/Modal";
import classes from "./classes.module.scss";
import { XMarkIcon } from "@heroicons/react/20/solid";
import Button, { EButtonVariant, EButtonColor } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import Input from "components/materials/Form/InputElement";
import { TextArea } from "components/materials/Form/TextAreaElement";
import VoteOptionInput from "./VoteOptionInput";
import Form, { useForm } from "components/materials/Form";
import { container } from "tsyringe";
import QuestionService from "services/projects/QuestionService";
import QuestionRequestResource from "common/resources/Project/Config/Vote/QuestionRequestResource";
import { useProjectId } from "hooks/useProjectId";
import { EQuestionCategory } from "common/enums/Project/Config/Vote/EQuestionCategory";
import QuestionResponseResource from "common/resources/Project/Config/Vote/QuestionResponseResource";
import { generateUniqueId } from "utils/UniqueIdUtils";
import ChoiceRequestResource from "common/resources/Project/Config/Choice/ChoiceRequestResource";
import ChoiceService from "services/projects/ChoiceService";

const questionService = container.resolve(QuestionService);
const choiceService = container.resolve(ChoiceService);

type IFormData = { [key: string]: unknown };
type IProps = {
	category: EQuestionCategory;
	onCreated: (vote: QuestionResponseResource) => void;
	onClose: () => void;
};

export default function CreateVoteModal(props: IProps) {
	const { clearErrors, errors, handleError } = useForm();
	const { voteOptions, onRemoveOption, onAddOption } = useVoteOptions();
	const { createVote } = useCreateVote(props.category, voteOptions);

	const onSubmit = useCallback(
		async (formData: IFormData) => {
			try {
				clearErrors();
				const createdVote = await createVote(formData);
				props.onCreated(createdVote);
			} catch (error) {
				handleError(error);
			}
		},
		[clearErrors, createVote, handleError, props],
	);

	return (
		<Form onSubmit={onSubmit} errors={errors}>
			<Modal isOpen onClose={props.onClose} className={classes["root"]}>
				<div className={classes["header"]}>
					<Typography type="p" size="large" weight="bold">
						{I18n.asset.pages.professional_space.tabs.votes.create_vote_modal.title}
					</Typography>
					<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
				</div>

				<div className={classes["form-container"]}>
					<TextArea name="content" label={I18n.asset.pages.professional_space.tabs.votes.create_vote_modal.vote_description} className={classes["input"]} required />
					<Input
						name="endDate"
						type="datetime-local"
						label={I18n.asset.pages.professional_space.tabs.votes.create_vote_modal.vote_end_date}
						className={classes["input"]}
						required
					/>
					<div className={classes["options-container"]}>
						<Typography type="p" size="small" weight="medium">
							{I18n.asset.pages.professional_space.tabs.votes.create_vote_modal.vote_options}
						</Typography>
						<div className={classes["options"]}>
							{voteOptions.map((value, index) => (
								<VoteOptionInput key={value} id={value} index={index} onRemove={onRemoveOption} />
							))}
						</div>
					</div>
					<Button variant={EButtonVariant.OUTLINED} color={EButtonColor.PRIMARY} onClick={onAddOption}>
						{I18n.asset.pages.professional_space.tabs.votes.create_vote_modal.vote_add_option}
					</Button>
					{errors.length > 0 && (
						<Typography type="p" size="small" weight="medium" color="error">
							{I18n.asset.pages.professional_space.tabs.votes.create_vote_modal.vote_option_max_length_error}
						</Typography>
					)}
				</div>

				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose}>
						{I18n.asset.pages.professional_space.tabs.votes.create_vote_modal.cancel}
					</Button>
					<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} type="submit">
						{I18n.asset.pages.professional_space.tabs.votes.create_vote_modal.create_vote}
					</Button>
				</div>
			</Modal>
		</Form>
	);
}

const useCreateVote = (category: EQuestionCategory, voteOptions: string[]) => {
	const { projectId } = useProjectId();

	const buildQuestionResource = useCallback(
		(formData: IFormData): QuestionRequestResource => {
			return QuestionRequestResource.hydrate<QuestionRequestResource>({
				content: formData["content"] as string,
				category: category,
				endsAt: new Date(formData["endDate"] as string),
			});
		},
		[category],
	);

	const extractChoicesFromForm = useCallback((formData: IFormData, voteOptions: string[]): { label: string; image?: File }[] => {
		return voteOptions
			.map((optionId) => {
				const label = formData[`option-${optionId}`] as string;
				const image = (formData[`option-image-${optionId}`] as FileList)?.[0];
				return label ? { label, image } : null;
			})
			.filter(Boolean) as { label: string; image?: File }[];
	}, []);

	const hydrateAndValidateChoices = useCallback(async (choices: { label: string; image?: File }[]) => {
		const hydratedChoices = ChoiceRequestResource.hydrateArray<ChoiceRequestResource>(choices);

		const validations = hydratedChoices.map((choice) => choice.validateOrReject());
		await Promise.all(validations);
		return hydratedChoices;
	}, []);

	const createChoices = useCallback(
		async (projectId: string, questionId: string, hydratedChoices: ChoiceRequestResource[], originalChoices: { label: string; image?: File }[]) => {
			await Promise.all(
				hydratedChoices.map((choice, index) => {
					const image = originalChoices[index]?.image;
					return choiceService.create(projectId, questionId, choice, image);
				}),
			);
		},
		[],
	);

	const createVote = useCallback(
		async (formData: IFormData) => {
			const questionResource = buildQuestionResource(formData);
			const choiceInputs = extractChoicesFromForm(formData, voteOptions);

			await QuestionRequestResource.validateOrReject(questionResource);
			const hydratedChoices = await hydrateAndValidateChoices(choiceInputs);

			const createdQuestion = await questionService.create(projectId, questionResource);
			await createChoices(projectId, createdQuestion.id, hydratedChoices, choiceInputs);

			return createdQuestion;
		},
		[projectId, voteOptions, buildQuestionResource, extractChoicesFromForm, hydrateAndValidateChoices, createChoices],
	);

	return { createVote };
};

const useVoteOptions = () => {
	const [voteOptions, setVoteOptions] = useState<string[]>([generateUniqueId(), generateUniqueId(), generateUniqueId()]);

	const onRemoveOption = useCallback((id: string) => {
		setVoteOptions((prev) => prev.filter((optionId) => optionId !== id));
	}, []);

	const onAddOption = useCallback(() => {
		setVoteOptions((prev) => [...prev, generateUniqueId()]);
	}, []);

	return { voteOptions, onRemoveOption, onAddOption };
};
